import type { ReactNode } from 'react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  useMergeRefs,
  hide,
  limitShift,
  safePolygon,
} from '@floating-ui/react';

import type { Placement as StylesPlacement, TooltipVariant } from './styles';
import { TooltipWrapper, TooltipContent } from './styles';

// Map floating-ui placements to our existing style placements
type FloatingPlacement = 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

type Placement = StylesPlacement;

const mapPlacementToFloating = (placement: Placement): FloatingPlacement => {
  const placementMap: Record<Placement, FloatingPlacement> = {
    top: 'top',
    bottom: 'bottom',
    left: 'left',
    right: 'right',
    'top-left': 'top-end',
    'top-right': 'top-start',
    'bottom-left': 'bottom-end',
    'bottom-right': 'bottom-start',
  };
  return placementMap[placement];
};

const isReactNodeEmpty = (node: ReactNode): boolean => {
  if (node === null || node === undefined || node === false) {
    return true;
  }
  if (Array.isArray(node)) {
    return node.every(isReactNodeEmpty);
  }
  if (typeof node === 'string') {
    return node.trim() === '';
  }
  return false;
};

export type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  className?: string;
  placement?: Placement;
  enabled?: boolean;
  maxWidth?: number;
  minWidth?: number;
  variant?: TooltipVariant;
  debounce?: number;
  debounceOnLeave?: number;
  visible?: boolean;
  onVisible?: () => void;
  fullWidth?: boolean;
  contentRef?: React.RefObject<HTMLDivElement>;
  cursor?: React.CSSProperties['cursor'];
  withOutsideClick?: boolean;
  onOutsideClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  dataAutomation?: string;
  hideOnScroll?: boolean;
  offset?: number;
};

export const Tooltip = ({
  content,
  children,
  className,
  placement = 'top',
  enabled = true,
  maxWidth = 180,
  minWidth = 0,
  variant,
  debounce = 0,
  debounceOnLeave = 0,
  onVisible,
  visible: visibleProp,
  fullWidth,
  contentRef,
  cursor = 'pointer',
  withOutsideClick = false,
  onOutsideClick,
  dataAutomation,
  hideOnScroll = true,
  offset: offsetValue = 12,
}: TooltipProps) => {
  const isContentEmpty = useMemo(() => isReactNodeEmpty(content), [content]);
  const isTooltipEnabled = enabled && !isContentEmpty;

  const [isOpen, setIsOpen] = useState(visibleProp ?? false);

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (open) {
        onVisible?.();
      }
      if (!open && onOutsideClick) {
        onOutsideClick();
      }
    },
    placement: mapPlacementToFloating(placement),
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
    middleware: [
      offset(offsetValue),
      flip(),
      shift({
        padding: 8,
        limiter: limitShift({
          offset: 5,
        }),
      }),
      hide({
        padding: 5,
      }),
    ],
  });

  const hover = useHover(context, {
    enabled: isTooltipEnabled && !withOutsideClick,
    delay: { open: debounce, close: Math.max(debounceOnLeave, 50) },
    handleClose: safePolygon({ buffer: 4 }),
    restMs: 40,
    move: false,
    mouseOnly: true,
  });

  const focus = useFocus(context, {
    enabled: isTooltipEnabled && !withOutsideClick,
  });

  const dismiss = useDismiss(context, {
    enabled: withOutsideClick,
  });

  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  useEffect(() => {
    if (visibleProp !== undefined) {
      setIsOpen(visibleProp);
    }
  }, [visibleProp]);

  useEffect(() => {
    if (!isTooltipEnabled) {
      setIsOpen(false);
    }
  }, [isTooltipEnabled]);

  useEffect(() => {
    if (!isOpen || !hideOnScroll) {
      return;
    }

    const handleInteraction = () => {
      setIsOpen(false);
    };

    const scrollableParents: EventTarget[] = [window];

    let element = refs.domReference.current?.parentElement;
    while (element) {
      const { overflow, overflowX, overflowY } = getComputedStyle(element);
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowX === 'auto' ||
        overflowX === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      ) {
        scrollableParents.push(element);
      }
      element = element.parentElement;
    }

    const events = ['scroll', 'wheel', 'touchmove'];
    scrollableParents.forEach((parent) => {
      events.forEach((eventType) => {
        parent.addEventListener(eventType, handleInteraction, { passive: true });
      });
    });

    return () => {
      scrollableParents.forEach((parent) => {
        events.forEach((eventType) => {
          parent.removeEventListener(eventType, handleInteraction);
        });
      });
    };
  }, [isOpen, hideOnScroll, refs.domReference]);

  const mergedFloatingRef = useMergeRefs([refs.setFloating, contentRef]);

  const isHidden = middlewareData.hide?.referenceHidden || middlewareData.hide?.escaped;

  return (
    <TooltipWrapper
      className={className}
      enabled={isTooltipEnabled}
      fullWidth={fullWidth}
      cursor={cursor}
      ref={refs.setReference}
      {...getReferenceProps()}
    >
      {children}
      {isOpen && !isHidden ? (
        <FloatingPortal>
          <TooltipContent
            ref={mergedFloatingRef}
            placement={placement}
            minWidth={minWidth}
            maxWidth={maxWidth}
            variant={variant}
            data-automation={dataAutomation}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {content}
          </TooltipContent>
        </FloatingPortal>
      ) : null}
    </TooltipWrapper>
  );
};
